import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	UserRoles,
	TLanguageIDs,
	IGetCategoryInfoV2,
	IGetCategoryRestaurantProperties,
	IGetCategoryRestaurantPropertiesArguments,
} from 'types';
import { viewCategoryInitialState, ViewCategoryContext, TTypeOfModalWindow } from './viewCategoryContext';
import { viewCategoryReducer } from './viewCategoryReducer';
import { ViewCategoryActionTypes } from './viewCategoryActions';
import { useNamedHeader } from 'hooks/useNamedHeader';
import { useFetchData } from 'hooks/useFetchData';
import { getCategoryInfoV2, getCategoryRestaurantProperties } from 'backend/categories';
import { useRoleAccess } from 'hooks/useRoleAccess';

export const ViewCategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(viewCategoryReducer, viewCategoryInitialState);
	const { showLoader, hideLoader, loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const params = new URL(document.location.href).searchParams;
	const categoryID = params.get('categoryID');

	const {
		response: categoryFullInfoResponse,
		loading: categoryFullInfoLoading,
		refetch: categoryFullInfoRefetch,
	} = useFetchData<IGetCategoryInfoV2, string>(categoryID ? getCategoryInfoV2 : null, categoryID || '');

	const {
		response: restaurantPropertiesResponse,
		loading: restaurantPropertiesLoading,
		refetch: restaurantPropertiesRefetch,
	} = useFetchData<IGetCategoryRestaurantProperties, IGetCategoryRestaurantPropertiesArguments>(
		state.restaurantIdForRestaurantProps && state.categoryFullInfo?.id ? getCategoryRestaurantProperties : null,
		{
			restaurantId: state.restaurantIdForRestaurantProps || '',
			categoryId: state.categoryFullInfo?.id || '',
		},
	);

	useNamedHeader(categoryFullInfoResponse?.data.name);

	useEffect(() => {
		categoryFullInfoLoading || restaurantPropertiesLoading ? showLoader() : hideLoader();
	}, [categoryFullInfoLoading, restaurantPropertiesLoading]);

	useEffect(() => {
		if (categoryFullInfoResponse) {
			const userRestaurantIDs = loggedUser.restaurants
				? loggedUser.restaurants.map((restaurant) => restaurant.id)
				: [];

			const accessibleRestaurants = hasAccess
				? categoryFullInfoResponse.data.restaurants
				: categoryFullInfoResponse.data.restaurants.filter((restaurant) =>
						userRestaurantIDs.includes(restaurant.id),
				  );

			dispatch({
				type: ViewCategoryActionTypes.SET_CATEGORY_FULL_INFO,
				payload: {
					categoryFullInfo: categoryFullInfoResponse.data,
					accessibleRestaurants,
				},
			});
		}
	}, [loggedUser.restaurants, hasAccess, categoryFullInfoResponse]);

	useEffect(() => {
		if (restaurantPropertiesResponse) {
			dispatch({
				type: ViewCategoryActionTypes.SET_RESTAURANT_PROPS,
				payload: {
					restaurantProps: restaurantPropertiesResponse.data,
				},
			});
		}
	}, [restaurantPropertiesResponse]);

	useEffect(() => {
		if (state.needRefetchData) {
			categoryFullInfoRefetch();
			dispatch({
				type: ViewCategoryActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		restaurantPropertiesRefetch();
	}, [state.restaurantIdForRestaurantProps]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: ViewCategoryActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const setRestaurantIdForRestaurantProps = useCallback(
		(value: Nullable<string>) => {
			dispatch({
				type: ViewCategoryActionTypes.SET_RESTAURANT_ID_FOR_RESTAURANT_PROPS,
				payload: {
					restaurantIdForRestaurantProps: value,
					restaurantProps: value ? state.restaurantProps : null,
				},
			});
		},
		[state.restaurantProps],
	);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: ViewCategoryActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: ViewCategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<ViewCategoryContext.Provider
			value={{
				refetchData,
				setRestaurantIdForRestaurantProps,
				setTranslationTab,
				changeTypeOfModalWindow,
				needRefetchData: state.needRefetchData,
				categoryFullInfo: state.categoryFullInfo,
				accessibleRestaurants: state.accessibleRestaurants,
				restaurantIdForRestaurantProps: state.restaurantIdForRestaurantProps,
				restaurantProps: state.restaurantProps,
				selectedTranslationTab: state.selectedTranslationTab,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</ViewCategoryContext.Provider>
	);
};
